import {
    closestCorners,
    getScrollableAncestors,
    getFirstCollision,
    KeyboardCode,
    DroppableContainer,
    KeyboardCoordinateGetter
} from '@dnd-kit/core'
import { subtract } from '@dnd-kit/utilities'
import debugModule from 'debug'

import { DragData, ParsedRow, isSpecialRow } from './types'

const debug = debugModule('app:SchemaBuilder:keyboard')

const directions: string[] = [
    KeyboardCode.Down,
    KeyboardCode.Right,
    KeyboardCode.Up,
    KeyboardCode.Left
]

interface KeyboardCoordinatesArgs {
    parsedRows: ParsedRow[]
    isRowFull: (rowIndex: number, excludeElementId?: string) => boolean
}

/**
 * Custom keyboard coordinate getter for the form builder.
 * Based on sortableKeyboardCoordinates but with additional filtering
 * based on what's being dragged:
 * - Layout items can only target other layout items
 * - Elements can only target elements or empty non-special rows
 */
export const createKeyboardCoordinates = ({
    parsedRows,
    isRowFull
}: KeyboardCoordinatesArgs): KeyboardCoordinateGetter => {
    const keyboardCoordinates: KeyboardCoordinateGetter = (
        event,
        {
            context: {
                active,
                collisionRect,
                droppableRects,
                droppableContainers,
                over,
                scrollableAncestors
            }
        }
    ) => {
        if (directions.includes(event.code)) {
            event.preventDefault()

            if (!active || !collisionRect) {
                return
            }

            const activeData = active.data.current as DragData | undefined
            const filteredContainers: DroppableContainer[] = []

            // Find which row the active element is in (for existing elements)
            let activeElementRowIndex: number | null = null
            if (activeData?.type === 'element' && !activeData.fromToolbox) {
                activeElementRowIndex = activeData.rowIndex ?? null
            }

            droppableContainers.getEnabled().forEach((entry) => {
                if (!entry || entry?.disabled) {
                    return
                }

                const rect = droppableRects.get(entry.id)
                if (!rect) {
                    return
                }

                const entryData = entry.data.current as DragData | undefined

                // Skip toolbox items - they can't receive drops
                if (entryData?.fromToolbox) {
                    return
                }

                // Apply type-based filtering
                if (activeData?.type === 'layout') {
                    // When dragging a layout item, only allow other layout items
                    if (entryData?.type !== 'layout' && entry.id !== 'form-builder-droppable') {
                        return
                    }
                } else if (activeData?.type === 'element') {
                    // When dragging an element, filter based on target type
                    if (entryData?.type === 'layout') {
                        // Never allow rows as targets for elements.
                        // Elements should target other elements (including empty row droppables).
                        return
                    } else if (entryData?.type === 'element') {
                        // Allow elements, but filter out elements in full/special rows (when from different row)
                        if (entryData.rowIndex !== undefined) {
                            // Check if target row is a special row
                            const targetRow = parsedRows[entryData.rowIndex]
                            if (targetRow && isSpecialRow(targetRow)) {
                                return
                            }

                            const isMovingWithinSameRow = activeElementRowIndex === entryData.rowIndex
                            if (!isMovingWithinSameRow && isRowFull(entryData.rowIndex, active.id as string)) {
                                return
                            }
                        }
                    } else {
                        // Unknown type, skip
                        return
                    }
                }

                // Directional filtering based on arrow key pressed
                switch (event.code) {
                    case KeyboardCode.Down:
                        if (collisionRect.top < rect.top) {
                            filteredContainers.push(entry)
                        }
                        break
                    case KeyboardCode.Up:
                        if (collisionRect.top > rect.top) {
                            filteredContainers.push(entry)
                        }
                        break
                    case KeyboardCode.Left:
                        if (collisionRect.left > rect.left) {
                            filteredContainers.push(entry)
                        }
                        break
                    case KeyboardCode.Right:
                        if (collisionRect.left < rect.left) {
                            filteredContainers.push(entry)
                        }
                        break
                }
            })

            const collisions = closestCorners({
                active,
                collisionRect: collisionRect,
                droppableRects,
                droppableContainers: filteredContainers,
                pointerCoordinates: null
            })
            let closestId = getFirstCollision(collisions, 'id')

            debug('Keyboard navigation', {
                keyCode: event.code,
                activeId: active.id,
                activeType: activeData?.type,
                overId: over?.id,
                filteredContainerIds: filteredContainers.map(c => c.id),
                closestId
            })

            // If the closest is the current over target and there are other options, pick the next one
            if (closestId === over?.id && collisions.length > 1) {
                closestId = collisions[1].id
                debug('Skipped current over, new closestId:', closestId)
            }

            if (closestId != null) {
                const activeDroppable = droppableContainers.get(active.id)
                const newDroppable = droppableContainers.get(closestId)
                const newRect = newDroppable ? droppableRects.get(newDroppable.id) : null
                const newNode = newDroppable?.node.current

                if (newNode && newRect && activeDroppable && newDroppable) {
                    const newScrollAncestors = getScrollableAncestors(newNode)
                    const hasDifferentScrollAncestors = newScrollAncestors.some(
                        (element, index) => scrollableAncestors[index] !== element
                    )
                    const hasSameContainer = isSameContainer(activeDroppable, newDroppable)
                    const isAfterActive = isAfter(activeDroppable, newDroppable)

                    const offset =
                        hasDifferentScrollAncestors || !hasSameContainer
                            ? { x: 0, y: 0 }
                            : {
                                x: isAfterActive ? collisionRect.width - newRect.width : 0,
                                y: isAfterActive ? collisionRect.height - newRect.height : 0
                            }

                    const rectCoordinates = {
                        x: newRect.left,
                        y: newRect.top
                    }

                    const newCoordinates =
                        offset.x && offset.y
                            ? rectCoordinates
                            : subtract(rectCoordinates, offset)

                    return newCoordinates
                }
            }
        }

        return undefined
    }

    return keyboardCoordinates
}

/**
 * Check if two droppables are in the same sortable container
 */
function isSameContainer(a: DroppableContainer, b: DroppableContainer): boolean {
    const aData = a.data.current as Record<string, unknown> | undefined
    const bData = b.data.current as Record<string, unknown> | undefined

    // Check if both have sortable data
    const aSortable = aData?.sortable as Record<string, unknown> | undefined
    const bSortable = bData?.sortable as Record<string, unknown> | undefined

    if (!aSortable || !bSortable) {
        return false
    }

    return aSortable.containerId === bSortable.containerId
}

/**
 * Check if droppable b comes after droppable a in the same container
 */
function isAfter(a: DroppableContainer, b: DroppableContainer): boolean {
    const aData = a.data.current as Record<string, unknown> | undefined
    const bData = b.data.current as Record<string, unknown> | undefined

    const aSortable = aData?.sortable as Record<string, unknown> | undefined
    const bSortable = bData?.sortable as Record<string, unknown> | undefined

    // Must have sortable data and be in same container
    if (!aSortable || !bSortable) {
        return false
    }

    if (aSortable.containerId !== bSortable.containerId) {
        return false
    }

    return (aSortable.index as number) < (bSortable.index as number)
}
